import moment from "moment";
import {
  getDuration,
  calculateDistance,
  eventsType,
  MOVEMENT_STATUS_PRESENTATION,
  getTimeGap,
  ignitionStatus,
} from "../utils";
// Canonical sidebar movement resolution (spec `vehicle-realtime-data-consistency`,
// task 3.8 / design step 8). Kept in its own module so it stays pure and testable
// without this component's map/component import graph.
import { formatCanonicalSpeed, getListStatusTime, resolveListCardMovement } from "./listCardMovement";
import spriteNative from "../assets/nativeIcons/icons.svg";
import sprite from "../../../assets/icons.svg";
import { vehiclePlaceholder, devicePlaceholder } from "../../../assets";
import { VideoEvidenceButton } from "./Button";
import { getLocalizedString } from "../../../shared/translation";
import { useGetDevicesQuery } from "../../ManagementPortal/services";

const getDeviceIds = (item) =>
  String(item?.device_name_list || item?.device_id || item?.source_id || "")
    .split(",")
    .map((id) => id.trim())
    .filter((id) => id && id !== "N/A");

const getFallbackSpeedMps = (rawSpeed, displaySpeed) => {
  if (rawSpeed !== undefined && rawSpeed !== null && rawSpeed !== "") {
    return rawSpeed;
  }

  if (typeof displaySpeed === "number") {
    return displaySpeed;
  }

  if (typeof displaySpeed !== "string") {
    return undefined;
  }

  const numericSpeed = Number(displaySpeed.match(/-?\d+(\.\d+)?/)?.[0]);
  if (!Number.isFinite(numericSpeed)) {
    return undefined;
  }

  return displaySpeed.toLowerCase().includes("km/h") ? numericSpeed / 3.6 : numericSpeed;
};

export const ListCard = ({
  data,
  cardType,
  onRowClick,
  setHighlight,
  selectedRowId,
  mapData,
  sortedDataDisplay,
  formatLastUpdated,
  // Canonical telemetry getters from `useVehicleTelemetry` (VehicleContainer /
  // AlternateIndex). Absent for the Devices/Trips/Events/Activities lists, which
  // keep their existing behavior.
  telemetry,
}) => {
  // Latest tracking packets omit model metadata; resolve only the visible devices.
  const deviceIds = [
    ...new Set((data || []).filter((item) => !item?.device_type).flatMap(getDeviceIds)),
  ];
  const { currentData: devices } = useGetDevicesQuery(
    { page: 1, per_page: deviceIds.length, device_ids: deviceIds.join(",") },
    { skip: !["Devices", "Vehicles"].includes(cardType) || !deviceIds.length },
  );
  const deviceTypes = new Map(
    devices?.data?.map((device) => [String(device.device_id), device.device_type]),
  );

  const renderCard = data?.map((item, id) => {
    const {
      status,
      plate_number,
      duration,
      start_time,
      end_time,
      events,
      activities,
      source_time,
      device_name_list,
      device_name,
      device_id,
      gps_distance,
      data,
      mode,
      ignition,
      speed,
      rawSpeed,
      lastUpdated,
      source_id,
      movementStatus,
    } = item || {};

    const liveMapData = mapData
      ? mapData[device_name_list] || mapData[device_id] || mapData[source_id] || {}
      : {};
    const hasLiveStatusData =
      liveMapData.statusTime !== undefined ||
      liveMapData.sourceTime ||
      liveMapData.serverTime ||
      liveMapData.ignition !== undefined ||
      liveMapData.rawSpeed !== undefined ||
      liveMapData.speed !== undefined;
    const fallbackIgnition = liveMapData.ignition ?? ignition;
    const fallbackTime = getListStatusTime(cardType, liveMapData, lastUpdated, source_time);
    const fallbackSpeed = getFallbackSpeedMps(
      liveMapData.rawSpeed ?? rawSpeed,
      liveMapData.speed || speed
    );

    /**
     * Canonical movement of this row (task 3.8). Nothing is recomputed from the
     * raw latest-vehicles fields or the realtime `mapData` map when the store
     * knows the plate; the legacy values above are only the fallback for a plate
     * the store has not reconciled.
     */
    const canonical = resolveListCardMovement({
      telemetry,
      plateNumber: plate_number,
      fallback: {
        observedAt: fallbackTime,
        deviceDataAt: lastUpdated,
        rawSpeed: fallbackSpeed,
        ignition: fallbackIgnition,
        movementStatus: hasLiveStatusData ? undefined : movementStatus,
      },
    });
    // Status icon and duration label share this single timestamp (bugfix.md 2.4):
    // A missing speed observation leaves the asset Offline/Never.
    const statusTime = canonical.observedAt;
    // Status itself is getVehicleMovementStatus(ignition.value, movement.speed,
    // movement.observedAt), resolved once in `resolveListCardMovement`.
    const resolvedMovementStatus = canonical.movementStatus;
    // Sidebar speed is the canonical movement.speed, presented with the existing
    // m/s -> km/h conversion and the "-"/"NA" fallback (design 3.3).
    const displaySpeed = canonical.isCanonical
      ? formatCanonicalSpeed(canonical.rawSpeed)
      : speed || null;
    const normalizedMovementStatus =
      resolvedMovementStatus === "stopped" ? "parked" : resolvedMovementStatus;
    const movementStatusPresentation =
      MOVEMENT_STATUS_PRESENTATION[normalizedMovementStatus] ||
      MOVEMENT_STATUS_PRESENTATION.offline;
    const movementStatusLabel = getLocalizedString(
      movementStatusPresentation.localizationKey,
      movementStatusPresentation.defaultLabel
    );
    const { activeWithin } = getTimeGap(statusTime) || {};

    const types = item?.device_type
      ? [item.device_type]
      : getDeviceIds(item).map((id) => deviceTypes.get(id));
    const deviceIcon = types.some((type) => type === "android" || type === "ios") ? "mobile" : "tracker";

    const getEventTypeStatus = () => {
      if (events?.status) {
        return eventsType(events?.status);
      } else return "";
    };

    const getSubType = () => {
      if (events?.sub_type && events?.sub_type !== events?.status) {
        return "(" + eventsType(events?.sub_type) + ")";
      } else return "";
    };

    const getEventName = () => {
      return (
        events?.config?.name ||
        events?.config?.Config?.name ||
        activities?.data?.config?.Config?.name ||
        activities?.data?.config?.name
      );
    };

    const getEventTime = (events, source_time) => {
      if (events?.sub_type === "geofence" && events?.type === "speeding") {
        if (events?.status === "ended")
          return moment(events?.end_time).format(" DD/MM/YYYY - hh:mm:ss A");
        else return moment(events?.start_time).format(" DD/MM/YYYY - hh:mm:ss A");
      }

      if (events?.sub_type === "time limit exceeded")
        return moment(events?.start_time).format(" DD/MM/YYYY - hh:mm:ss A");

      if (events?.type === "geofence" && events?.status === "inside") {
        return moment(source_time).format(" DD/MM/YYYY - hh:mm:ss A");
      }

      if (events?.type === "geofence" && events?.status === "exit") {
        return moment(events?.exit_time || events?.end_time).format(" DD/MM/YYYY - hh:mm:ss A")
      }

      if (events?.type === "geofence" && events?.status === "entry") {
        return moment(events?.entry_time || events?.start_time).format(" DD/MM/YYYY - hh:mm:ss A")
      }

      return moment(source_time).format(" DD/MM/YYYY - hh:mm:ss A");
    };

    const getTitle = (cardType) => {
      if (cardType === "Devices") {
        return `${item?.name} ( ${item?.device_manufacturer_name} - ${item?.device_model_name})`;
      } else if (cardType !== "Activities") {
        return plate_number;
      } else {
        return data?.plate_number;
      }
    };

    return (
      <li
        key={id}
        onClick={() => {
          onRowClick(item, id);
          setHighlight(id);
        }}
        className={`card-view-list_item ${
          cardType === "Vehicles" || cardType === "Devices"
            ? selectedRowId === `${item?.id}-${item?.plate_number}`
              ? "selected"
              : ""
            : selectedRowId === id
            ? "selected"
            : ""
        }`}
      >
        {/* Vehicles and Devices Status*/}
        {(cardType === "Vehicles" || cardType === "Devices") && (
          <div
            className={`status movement-status-list movement-status-list--${normalizedMovementStatus}`}
          >
            <span className={`movement-status-list_icon ${normalizedMovementStatus}`}>
              <img
                className="user-profile-image"
                src={cardType === "Vehicles" ? vehiclePlaceholder : devicePlaceholder}
              />
            </span>
            <span className="latest-data-time">{movementStatusLabel}</span>
            <span className={`activeWithin_${normalizedMovementStatus}`}>
              {activeWithin}
            </span>
          </div>
        )}

        {/* Trips and Activity */}
        {(cardType === "Trips" || cardType === "Activities") && (
          <div className="status">
            {((mode === "TRIP" || mode === "trip") &&
              (data?.status === "Completed" || data?.status === "ended")) ||
            status === "Completed" ||
            status === "ended" ? (
              <span className="status-completed">
                <svg className="icon">
                  <use href={`${sprite}#tick`}></use>
                </svg>
              </span>
            ) : mode === "PARKED" || mode === "parked" ? (
              <span className="status-parked">
                <svg className="icon">
                  <use href={`${sprite}#parking`}></use>
                </svg>
              </span>
            ) : (
              <span className="status-in-progress">
                <svg className="icon">
                  <use href={`${sprite}#time`}></use>
                </svg>
              </span>
            )}
          </div>
        )}
        <div
          className={`${
            cardType === "Vehicles"
              ? "vehicle-id"
              : cardType === "Devices"
              ? "device-id"
              : "card-view-list_item_info"
          }`}
        >
          <div className="card-view-list_item_info_title-bar">
            <h3
              className="item-id truncate"
              title={
                item?.plate_number ||
                item?.device_name_list ||
                item?.name ||
                item?.device_name ||
                item?.source_id
              }
            >
              {cardType === "Devices" ? (
                <>{item?.device_name_list || item?.name}</>
              ) : cardType !== "Activities" ? (
                cardType === "Vehicles" ? (
                  plate_number || "-"
                ) : (
                  plate_number ||
                  item?.other_data?.device_name ||
                  item?.events?.device_name ||
                  device_name ||
                  source_id
                )
              ) : (
                data?.plate_number || data?.device_name || data?.source_id
              )}
            </h3>

            {cardType === "Events" && events?.config?.video_evidence && (
              <VideoEvidenceButton
                plateNumber={plate_number}
                sourceTime={source_time}
                wrapperClassName="ml-auto"
                btnClassName="mb-2 md"
              />
            )}

            {cardType === "Trips" && (
              <p className="date-and-time">
                <div>
                  <div className="time-stamp trip-start-time">
                    {start_time && moment(start_time).format(" DD/MM/YY - hh:mm A")}
                  </div>
                  <div className="time-stamp trip-end-time">
                    {end_time ? moment(end_time).format(" DD/MM/YY - hh:mm A") : "In Progress"}
                  </div>
                </div>
              </p>
            )}
            {cardType === "Activities" && (
              <p className="date-and-time">
                <div>
                  <div className="time-stamp trip-start-time">
                    {data?.start_time && moment(data?.start_time).format(" DD/MM/YY - hh:mm A")}
                  </div>
                  <div className="time-stamp trip-end-time">
                    {data?.end_time
                      ? moment(data?.end_time).format(" DD/MM/YY - hh:mm A")
                      : "In Progress"}
                  </div>
                </div>
              </p>
            )}
            {cardType === "Events" && (
              <p className="date-and-time">
                <div className="time-stamp py-1">{getEventTime(events, source_time)}</div>
              </p>
            )}
          </div>

          {/* Trips, Events, Activities Time */}
          {cardType !== "Devices" && cardType !== "Vehicles" && (
            <section className="card-view-list_item_info_summary">
              <h4 className="device-name">
                {device_name || events?.device_name || data?.device_name || "-"}
              </h4>
              {(cardType === "Trips" || cardType === "Activities") && (
                <div className="trip-and-time">
                  <span className="trip">
                    {cardType === "Trips" && calculateDistance(gps_distance)}
                    {cardType === "Activities" && mode === "TRIP"
                      ? calculateDistance(data?.gps_distance)
                      : ""}
                  </span>
                  <span className="trip-time">
                    {getDuration(cardType !== "Activities" ? duration : data?.duration)}
                  </span>
                </div>
              )}
              {cardType === "Events" && (
                <>
                  <div className="events-and-status">
                    <span
                      className="event truncate"
                      title={eventsType(events?.type) + getSubType()}
                    >
                      {eventsType(events?.type)}
                      {getSubType()}
                    </span>
                    <span className="event-status truncate" title={getEventTypeStatus()}>
                      {getEventTypeStatus()}
                    </span>
                  </div>
                  <h4 className="event-status truncate" title={getEventName()}>
                    {getEventName()}
                  </h4>
                </>
              )}
            </section>
          )}
        </div>

        {cardType === "Vehicles" && (
          <div className="speed">
            {/* Existing sidebar fallback ("-") is kept for an unavailable speed. */}
            <div title={`${displaySpeed || "-"}`}>{`${displaySpeed || "-"}`}</div>
          </div>
        )}

        {cardType === "Devices" && (
          <div className="device-name ">
            <div className="truncate" title={device_name}>
              {device_name}
            </div>
          </div>
        )}

        {/* Vehicles and Devices ignition */}
        {(cardType === "Vehicles" || cardType === "Devices") && (
          <div className="ignition-status">
            <>
              <span className="icon-wrapper">
                <svg className="icon device">
                  <use href={`${spriteNative}#${deviceIcon}`}></use>
                </svg>
              </span>

              <span className={`${deviceIcon === "mobile" && `${status}_mobile`}`}></span>

              {ignitionStatus(ignition)}
            </>
          </div>
        )}
      </li>
    );
  });
  return <>{renderCard}</>;
};
